/**
 * Turns a stored tax profile value into the text the Tax Profile page shows, and turns what a
 * person typed back into the form the column stores.
 *
 * The api sends every value in the form its column holds it in: an EIN and a phone number are bare
 * digits, a date is YYYY-MM-DD, a month and day is MM-DD, a money or percent column reads back from
 * Postgres as a string, and a choice column holds its canonical token. Each function here is
 * picked by the catalog entry's `type`.
 *
 * A taxpayer number (`tin`) is the exception: the api sends it only masked to its last four digits
 * ('*****6789'), or as the four digits alone when they are all that is known ('6789'), and the only
 * nine bare digits the page ever holds are the ones a person typed, which are masked the same way
 * before they are shown.
 */

import type {
  TaxProfileFieldDefinition,
  TaxProfileBooleanLabels,
  TaxProfileFieldType,
} from '@shared/constants/taxProfileFields';
import type { PersonName } from '@shared/types/taxProfile';

/** What the value cell says when the column is empty. */
export const NOT_ON_FILE = 'Not on file';

/**
 * The months in order, January first: the month a value names, and the months ValuePicker offers
 * for a month field and in the month select of a month-and-day field.
 */
export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const SHORT_MONTH_NAMES = MONTH_NAMES.map((month) => month.slice(0, 3));

const digitsOf = (value: string) => value.replace(/\D/g, '');

/** '123456789' -> '12-3456789'. Anything that is not nine digits is shown as it is stored. */
export const formatEin = (value: string): string => {
  const digits = digitsOf(value);
  if (digits.length !== 9) return value;
  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
};

/**
 * What a taxpayer number of which only the last four digits are known reads as: a K-1 that prints
 * an individual partner's SSN truncated. Here rather than in wording.ts, like NOT_ON_FILE above,
 * because wording.ts imports this file.
 */
export const lastFourOnly = (digits: string): string =>
  `Last 4 only: ${digits}`;

/**
 * A taxpayer number as the page shows it: nine bare digits, which only a person's typing gives,
 * masked to '*****6789' the way the api masks what it sends; four bare digits, which is the api
 * sending a number of which only the last four are known (maskToLast4 leaves four digits as they
 * are), as lastFourOnly says it; anything else, which is the api's masked text, as it is.
 */
export const formatTaxpayerNumber = (value: string): string => {
  if (/^\d{9}$/.test(value)) return `*****${value.slice(5)}`;
  if (/^\d{4}$/.test(value)) return lastFourOnly(value);
  return value;
};

/** '4155550100' -> '(415) 555-0100'; eleven digits starting with 1 keep the country code. */
export const formatPhone = (value: string): string => {
  const digits = digitsOf(value);
  const national =
    digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (national.length !== 10) return value;
  const shown = `(${national.slice(0, 3)}) ${national.slice(
    3,
    6
  )}-${national.slice(6)}`;
  return digits.length === 11 ? `+1 ${shown}` : shown;
};

/** The first ten characters of an ISO date or timestamp, or null when there are none. */
const isoDatePart = (value: string): string | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return match ? match[0] : null;
};

/** '2021-03-04' -> 'Mar 4, 2021'. The digits are read straight, so no time zone shifts the day. */
export const formatDate = (value: string): string => {
  const isoDate = isoDatePart(value);
  if (!isoDate) return value;
  const [year, month, day] = isoDate.split('-').map(Number);
  const monthName = SHORT_MONTH_NAMES[month - 1];
  if (!monthName) return value;
  return `${monthName} ${day}, ${year}`;
};

/**
 * The date of a timestamp (approvedAt, submittedAt, decidedAt) in the reader's own time zone:
 * '2026-09-24T02:00:00.000Z' reads 'Sep 23, 2026' in California. formatDate would read
 * the UTC day straight out of the text, which is a day ahead for a US reader every evening. A value
 * that holds a date alone ('2026-09-24') names no time of day, so it is read straight as formatDate
 * does; a value that is neither is returned as it is stored.
 */
export const formatTimestampDate = (value: string): string => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) return formatDate(value);
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return value;
  // No timeZone option, so Intl uses the browser's own.
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(when);
};

/**
 * '2026-09-14T10:00:00.000Z' -> 'Sep 14, 2026, 10:00 AM' in the reader's own time zone, for the
 * Added column of the documents table, which needs the time because several files can arrive on the
 * same day. `withSeconds` adds the seconds, for that cell's title. A value that is not a timestamp
 * is returned as it is stored.
 */
export const formatDateTime = (value: string, withSeconds = false): string => {
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return value;
  const shown = new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: withSeconds ? 'medium' : 'short',
  }).format(when);
  // Some versions of the Unicode data Node and the browsers carry put a narrow no-break space
  // before AM and PM. Replacing it with an ordinary space keeps the text the same everywhere.
  return shown.replace(/[\u202f\u00a0]/g, ' ');
};

/** How many digits a stored decimal keeps once its trailing zeros are dropped. */
const storedDecimals = (value: string): number => {
  const fraction = /\.(\d+)$/.exec(value.trim());
  if (!fraction) return 0;
  return fraction[1].replace(/0+$/, '').length;
};

/** '1234567' -> '$1,234,567'; '0.000100' -> '$0.0001'; '1234.5' -> '$1,234.50'. */
export const formatMoney = (value: string | number): string => {
  const asText = String(value).trim();
  const amount = Number(asText);
  if (!asText || Number.isNaN(amount)) return asText;
  const decimals = storedDecimals(asText);
  const shownDecimals = decimals === 0 ? 0 : Math.min(Math.max(decimals, 2), 6);
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: shownDecimals,
    maximumFractionDigits: shownDecimals,
  });
};

/** '30.50' -> '30.5%'; '50.00' -> '50%'. */
export const formatPercent = (value: string | number): string => {
  const asText = String(value).trim();
  const amount = Number(asText);
  if (!asText || Number.isNaN(amount)) return asText;
  const decimals = storedDecimals(asText);
  return `${amount.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.min(decimals, 4),
  })}%`;
};

/** '10000000' -> '10,000,000'. A count that is not a number is shown as it is stored. */
export const formatCount = (value: string | number): string => {
  const asText = String(value).trim();
  const amount = Number(asText);
  if (!asText || Number.isNaN(amount)) return asText;
  return amount.toLocaleString('en-US');
};

/**
 * true / 'true' / 't' / '1' / 'yes' -> 'Yes'; the false side -> 'No'. With `labels` (a column's
 * booleanLabels), the true side reads labels.true and the false side labels.false instead.
 */
export const formatBoolean = (
  value: string | number | boolean,
  labels?: TaxProfileBooleanLabels
): string => {
  const yes = labels?.true ?? 'Yes';
  const no = labels?.false ?? 'No';
  if (typeof value === 'boolean') return value ? yes : no;
  const asText = String(value).trim().toLowerCase();
  if (['true', 't', '1', 'yes', 'y'].includes(asText)) return yes;
  if (['false', 'f', '0', 'no', 'n'].includes(asText)) return no;
  return String(value);
};

/** 12 -> 'December'. A month outside 1..12 is shown as it is stored. */
export const formatMonth = (value: string | number): string => {
  const month = Number(String(value).trim());
  return MONTH_NAMES[month - 1] ?? String(value);
};

/**
 * How many days a month can have, February counted with its 29th, because a month and day stored
 * without a year can be February 29.
 */
export const daysInMonth = (month: number): number =>
  [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1] ?? 31;

/**
 * The month and day of a value stored as MM-DD, as numbers, or null when the value is not one. A
 * full YYYY-MM-DD date is read too, for its month and day: a value read off a document before
 * tax_profiles.tax_year_start held MM-DD still carries its year.
 */
export const monthDayOf = (
  value: string
): { month: number; day: number } | null => {
  const match = /^(?:\d{4}-)?(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const month = Number(match[1]);
  const day = Number(match[2]);
  if (!MONTH_NAMES[month - 1] || day < 1 || day > daysInMonth(month)) {
    return null;
  }
  return { month, day };
};

/** '01-01' -> 'January 1'. A value that is not a month and day is shown as it is stored. */
export const formatMonthDay = (value: string): string => {
  const parts = monthDayOf(value);
  return parts ? `${MONTH_NAMES[parts.month - 1]} ${parts.day}` : value;
};

/** 1 and 5 -> '01-05', the form a month and day is stored in. */
export const monthDayText = (month: number, day: number): string =>
  `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

/** The choice's label from the catalog entry, or the stored token when the catalog has no label. */
export const formatChoice = (
  value: string,
  field: Pick<TaxProfileFieldDefinition, 'choices'>
): string =>
  field.choices?.find((choice) => choice.value === value)?.label ?? value;

const formatByType = (
  value: string | number | boolean,
  type: TaxProfileFieldType,
  field: Pick<TaxProfileFieldDefinition, 'choices' | 'booleanLabels'>
): string => {
  switch (type) {
    case 'ein':
      return formatEin(String(value));
    case 'tin':
      return formatTaxpayerNumber(String(value));
    case 'phone':
      return formatPhone(String(value));
    case 'date':
      return formatDate(String(value));
    case 'money':
      return formatMoney(value as string | number);
    case 'percent':
      return formatPercent(value as string | number);
    case 'boolean':
      return formatBoolean(value, field.booleanLabels);
    case 'month':
      return formatMonth(value as string | number);
    case 'month_day':
      return formatMonthDay(String(value));
    case 'choice':
      return formatChoice(String(value), field);
    case 'number':
      return formatCount(value as string | number);
    case 'state':
    case 'year':
    case 'naics':
    case 'email':
    case 'text':
    case 'longtext':
    default:
      return String(value);
  }
};

/**
 * The text of a field's value cell: the stored value in the shape its catalog type calls for, and
 * NOT_ON_FILE when the column is empty.
 */
export const formatFieldValue = (
  value: string | number | boolean | null | undefined,
  field: Pick<TaxProfileFieldDefinition, 'type' | 'choices' | 'booleanLabels'>
): string => {
  if (value === null || value === undefined || value === '') return NOT_ON_FILE;
  return formatByType(value, field.type, field);
};

/** 'First Last', or an empty string when the api sent no person. */
export const formatPersonName = (person: PersonName | null | undefined) =>
  person ? `${person.first_name} ${person.last_name}`.trim() : '';

/**
 * The text ValuePicker's "Enter a different value" opens a value on.
 *
 * A select — a choice field, a yes / no field, a month — opens on the stored token itself, because
 * that is what its options carry. A date box needs YYYY-MM-DD, which is the stored form. Every
 * other box opens on the same text the cell showed, so clicking a value and pressing Enter without
 * touching it changes nothing: storedValueFromInput below turns that text back into the stored
 * form.
 */
export const editorTextFromStored = (
  value: string | number | boolean | null | undefined,
  field: Pick<TaxProfileFieldDefinition, 'type' | 'choices' | 'booleanLabels'>
): string => {
  if (value === null || value === undefined || value === '') return '';
  switch (field.type) {
    case 'choice':
      return String(value);
    case 'boolean':
      return formatBoolean(value) === 'Yes' ? 'true' : 'false';
    case 'month':
      return String(Number(String(value).trim()) || '');
    case 'date':
      return isoDatePart(String(value)) ?? String(value);
    // The box opens empty on the api's masked text, which is not a number anyone can keep, and on
    // the digits a person typed earlier in the row's review.
    case 'tin':
      return /^\d{9}$/.test(String(value)) ? String(value) : '';
    default:
      return formatByType(value, field.type, field);
  }
};

/** Drops everything that is not a digit, a minus sign or a decimal point, which formatting adds. */
const numericTextOf = (text: string) => text.replace(/[^\d.-]/g, '');

/**
 * What the api is sent for the text a person typed: the canonical form the column stores.
 *
 * Returns digits alone for an EIN or a phone number, the date box's YYYY-MM-DD, a number without
 * its thousands separators, currency sign or percent sign, the token of a chosen option, and a real
 * true or false for a yes / no field. An empty box returns null, which empties the column.
 *
 * Text the api's shape check will refuse is passed through as it was typed rather than repaired
 * here, so the api answers with the reason and the page shows it.
 */
export const storedValueFromInput = (
  text: string,
  field: Pick<TaxProfileFieldDefinition, 'type'>
): string | number | boolean | null => {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  switch (field.type) {
    case 'boolean':
      return ['true', 'yes', 'y', '1'].includes(trimmed.toLowerCase());
    case 'ein':
    case 'tin':
    case 'phone':
    case 'naics':
    case 'year':
      return trimmed.replace(/\D/g, '');
    case 'number':
    case 'money':
    case 'percent':
    case 'month': {
      const numeric = numericTextOf(trimmed);
      return Number.isNaN(Number(numeric)) || numeric === ''
        ? trimmed
        : numeric;
    }
    case 'state':
      return trimmed.length === 2 ? trimmed.toUpperCase() : trimmed;
    default:
      return trimmed;
  }
};
